import { ChangeDetectorRef, Component, ChangeDetectionStrategy } from '@angular/core';
import { untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { AuthService } from 'services/auth.service';
import { AdminGetUserDetailsParams, AdminGetUserDetailsResult } from '@cf-shared/admin-user-detail-interface';
import { unreadablePaymentHistoryText } from './admin-user-partial-result';
import { AdminChangeEmailParams } from '@cf-shared/admin-change-email-interface';
import {
  AdminChangeSubscriptionParams,
  AdminChangeSubscriptionResult,
  AdminChangeSubscriptionResult_GrantLegacyPrice,
  AdminChangeSubscriptionResult_Synchronize,
  AdminLegacyPriceGrantSummary,
  formatMinorAmount,
} from '@cf-shared/admin-change-subscription-interface';
import { isAdminEmailShapeValid } from '@cf-shared/admin-change-email';
import { normalizeEmail } from '@cf-shared/email-normalization';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { isPayProSubscriptionSyncInfoIdentical } from '@cf-shared/paypro-diffing';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { AdminUserSubscriptionPageParams } from './admin-user-subscription-interface';
import { CouchUserInfo, SubscriptionStatus, UserLicenseType } from '@cf-shared/data-model/couch-user';
import { buildSubscriptionOpsSummary, describeCoachCancelImpact, describeCoachResumeCost, limitRelatedAccountsForDisplay } from '@cf-shared/subscription-ops-summary';
import { formatPaymentHistoryLines } from '@cf-shared/subscription-payment-history';
import { resolveActionCapability } from '@cf-shared/subscription-action-capability';
import { describeErrorAndReportIfUnexpected } from 'util/version-mismatch-error-policy';
import { describeError } from '@cf-shared/error-describe';

/**
 * The opening step of an action that asks the operator nothing and starts
 * straight away. Re-sync is the one that uses it, because re-reading a
 * subscription from the provider changes nothing on its own — putting a
 * confirmation in front of it would be a behaviour change nobody asked for.
 *
 * It exists so that "asks nothing" is stated at the call site rather than shown
 * by leaving a field out. An OPTIONAL opening step would let a future action
 * declare an input type, omit the step, and silently receive `undefined` in
 * place of it — with the types still agreeing, on a screen that moves money.
 */
async function nothingToAskTheOperatorAsync(): Promise<undefined> {
  return undefined;
}

/**
 * One support action on this page, described only by what it does DIFFERENTLY
 * from the others. Everything the actions share — asking, the busy flag, the
 * call, the alert, the refresh, the failure ending — lives once in
 * `runSubscriptionActionAsync`.
 *
 * `TOperatorInput` is whatever that action's opening step hands on to the rest
 * of it: a plain `true` for an action that only confirms, a day count for Grant
 * Free Days, the worked-out figures for the legacy-price grant, and `undefined`
 * for Re-sync, which asks nothing at all.
 */
interface SubscriptionAction<TOperatorInput> {
  /**
   * What this action asks the operator BEFORE anything happens. Returns what the
   * rest of the action needs, or `null` to abandon without touching the account.
   *
   * Required, even for an action that asks nothing — that one supplies
   * `nothingToAskTheOperatorAsync`. Keeping it required is what makes
   * `buildParams` and `describeSuccess` below honest: they receive exactly what
   * this returns, so an action cannot name an input type it would never be given.
   */
  askOperatorAsync: () => Promise<TOperatorInput | null>;

  /** The call to make, built from whatever the opening step supplied. */
  buildParams: (operatorInput: TOperatorInput) => AdminChangeSubscriptionParams;

  /**
   * The alert heading when this action does not go through. Used both for the
   * backend's own plain-language refusal and, via `showActionFailureAsync`, for
   * a thrown call.
   */
  failureTitle: string;

  /**
   * What failed, written to follow the words "Could not " — e.g. 'cancel the
   * subscription'. Handed to `showActionFailureAsync`.
   */
  couldNotDo: string;

  /** The alert heading when it worked. */
  successTitle: string;

  /** The alert message when it worked. */
  describeSuccess: (result: AdminChangeSubscriptionResult, operatorInput: TOperatorInput) => string;
}

@Component({
    selector: 'page-admin-user-subscription',
    templateUrl: 'admin-user-subscription.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class AdminUserSubscriptionPage extends CsPage<AdminUserSubscriptionPageParams> {
  protected loading = true;
  protected userId: string;

  protected couchInfoText = '';
  protected payProInfoText = '';
  // The PayPro field-by-field diff, shown as text. It no longer gates the
  // re-sync button — that is the shared capability matrix's job now, so the
  // button works for every provider rather than only for a PayPro mismatch.
  protected syncedText = '';
  // Human-readable "what you are about to change" summary shown above the
  // action buttons: provider, status, trial flag, current next-charge/expiry.
  protected currentStateText = '';
  // "Other accounts for this person" summary — duplicate/split accounts are a
  // recurring support case (#4227, #4198, #4207) that neither front-end used
  // to surface.
  protected relatedAccountsText = '';
  // "What this customer actually paid" — charges, refunds and chargebacks on the
  // web rails (#4248 Phase 5). READ-ONLY: this card shows history, no button
  // here issues a refund.
  protected paymentHistoryText = '';
  // Whether the Grant Free Days button shows. True for ANY active subscription
  // (PayPro / Google Play / Apple / Paddle), not only a PPG-synced one (#3949).
  protected canGrantFreeDays = false;
  // Whether the "Cancel Subscription (end of period)" button shows. Driven by the
  // shared capability matrix (#4248): true for the web rails (Paddle / PayPro),
  // false for Apple / Google Play (which the customer must cancel in the store).
  protected canCancelSubscription = false;
  // Whether the "Re-sync from Provider" button shows. Driven by the shared
  // capability matrix (#4248 Phase 3): true for every provider we can re-read
  // (all four), as long as we hold the identifier that provider's reconciler
  // needs. This replaced a gate that only appeared when the PayPro diff came
  // back different — which meant a Paddle or Play customer whose state had
  // drifted could never be re-synced from this page at all.
  protected canResyncSubscription = false;
  // Whether the "Resume Subscription" button shows — the mirror image of Cancel
  // (#4248 Phase 4). Driven by the shared capability matrix, which only says yes
  // when there is genuinely something to undo: a pending cancel, or a Paddle
  // subscription whose pause already took effect, or a Suspended PayPro one.
  protected canResumeSubscription = false;
  // Whether the "Switch Plan" button shows (#4248 Phase 7) — Paddle only. The
  // matrix refuses PayPro (pointing at ChangeProduct), both stores, and any
  // Paddle subscription that is ended, paused, or already scheduled to change.
  protected canSwitchPlan = false;
  protected canGrantLegacyPrice = false;
  /** Why each WITHHELD action is unavailable, shown where its button would have
   *  been (#4362). Before this the page rendered nothing for a refused action,
   *  so an operator could not tell "we are not allowed to do this" from "there
   *  is nothing here to act on" — the same dead end the misleading CLI message
   *  created, in a quieter form. The reasons come from the shared capability
   *  matrix, so they read the same here, in the CLI and from the server. */
  protected unavailableActions: Array<{ action: string; reason: string }> = [];
  private userDetails: AdminGetUserDetailsResult = undefined;

  constructor(
    public navCtrl: NavController,
    private auth: AuthService,
    private cloudFunctions: CloudFunctionsService,
    private alertCtrl: AlertController,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }



  /**
   * Tell the operator that an action failed, and decide on the way what reaches
   * Sentry.
   *
   * Every action on this page ends the same way: the alert, the shared error
   * policy (`describeErrorAndReportIfUnexpected`), the fallback wording, and the
   * OK button. Only the title and the verb ever differ, so those are the
   * parameters and everything else lives here once. Why it was worth extracting
   * — six of eight handlers had silently drifted off the policy — is written up
   * in `design-docs/deferred-findings/2026-07-28T0930Z-officer-screen-version-refusal.md`.
   *
   * @param title        the alert's heading, e.g. 'Re-sync Failed'.
   * @param couldNotDo   what failed, written to follow the words "Could not " —
   *                     e.g. 're-sync from the provider'.
   * @param error        the caught error. The shared policy decides whether it
   *                     is the expected "this server is behind that account's
   *                     data" refusal (#4283), which is explained and NOT sent
   *                     to Sentry, or a genuine failure, which is reported and
   *                     shown using the wording built here.
   * @param followUpAdvice optional extra line telling the operator what to do
   *                     next. Shown on its own line under the message.
   */
  private async showActionFailureAsync(
    title: string,
    couldNotDo: string,
    error: unknown,
    followUpAdvice = '',
  ): Promise<void> {
    const adviceLine = followUpAdvice ? `\n${followUpAdvice}` : '';
    await this.alertCtrl.showAlertAsync<void>({
      title: untranslatedString(title),
      message: describeErrorAndReportIfUnexpected(
        error,
        this.auth.logging,
        untranslatedString(`Could not ${couldNotDo}: ${describeError(error)}${adviceLine}`),
      ),
      buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary }],
    });
  }

  /**
   * Run one support action from start to finish.
   *
   * Every action on this page that changes a subscription follows the same
   * sequence, and this is the one place it is written down: ask the operator
   * whatever this action asks (if anything) and stop if they back out, set the
   * busy flag and ask for a redraw, call the backend, show an alert carrying
   * either the backend's plain-language refusal or this action's success
   * message, re-read the account, and in a `finally` clear the busy flag and
   * redraw again.
   *
   * WHY IT IS WORTH SAYING ONCE. Before this, each action wrote the sequence out
   * in full, so every rule about how this page reports what happened had to be
   * remembered in each copy. That has already gone wrong here: the comment on
   * `showActionFailureAsync` records that six of eight handlers had silently
   * drifted off the shared error policy before anybody counted them. This screen
   * moves real money and real access, and the one outcome it must never produce
   * is an operator unable to tell "nothing needed changing" from "the call
   * failed".
   *
   * The single conditional alert below is what the actions do today. Most of
   * them branch on `result.error` into two separate alert calls with the same
   * buttons; `handleSyncSubscriptionButtonClick` already writes it as one. The
   * two produce identical output.
   */
  private async runSubscriptionActionAsync<TOperatorInput>(
    action: SubscriptionAction<TOperatorInput>,
  ): Promise<void> {
    // if the operator backed out of this action's opening step, touch nothing
    const operatorInput = await action.askOperatorAsync();
    if (operatorInput === null) {
      return;
    }

    this.loading = true;
    this.cdRef.markForCheck();

    try {
      const result = await this.cloudFunctions.adminChangeSubscriptionAsync(action.buildParams(operatorInput));
      // surface the backend's plain-language message on a refusal (a store or
      // PayPro refusal, a precondition, or a half-state "run Re-sync" notice) —
      // never a silent no-op.
      await this.alertCtrl.showAlertAsync<void>({
        title: untranslatedString(result.error ? action.failureTitle : action.successTitle),
        message: untranslatedString(result.error ? result.error : action.describeSuccess(result, operatorInput)),
        buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary }],
      });
      await this.loadAsync();
    } catch (error) {
      // a thrown callable (network failure, rejection) must not vanish into a
      // cleared spinner — that is the "stale account signed off as healthy"
      // outcome these actions exist to prevent. The shared policy decides the
      // wording and what reaches Sentry; see `showActionFailureAsync`.
      await this.showActionFailureAsync(action.failureTitle, action.couldNotDo, error);
    } finally {
      this.loading = false;
      this.cdRef.markForCheck();
    }
  }

  /**
   * Ask the operator a plain two-button yes/no before an action runs.
   *
   * Returns `true` to go ahead, and `null` for every other answer — declining,
   * or dismissing the alert without choosing — which is what the actions did
   * when each wrote its own `if (!confirmed) return;`.
   *
   * @param title        the alert's heading.
   * @param message      what the operator is agreeing to, including the
   *                     consequences that action wants stated in front of them.
   * @param declineLabel the wording on the button that abandons the action.
   * @param proceedLabel the wording on the button that runs it.
   */
  private async confirmActionAsync(
    title: string,
    message: string,
    declineLabel: string,
    proceedLabel: string,
  ): Promise<true | null> {
    const confirmed = await this.alertCtrl.showAlertAsync<boolean>({
      title: untranslatedString(title),
      message: untranslatedString(message),
      buttons: [
        { text: untranslatedString(declineLabel), role: AlertButtonRole.Cancel, result: false },
        { text: untranslatedString(proceedLabel), role: AlertButtonRole.Primary, result: true },
      ],
    });
    return confirmed ? true : null;
  }

  async loadAsync() {
    let apiParams: AdminGetUserDetailsParams = {
      userId: this.userId,
    };

    try {
      this.loading = true;

      let apiResults = await this.cloudFunctions.adminGetUserDetailsAsync(apiParams);
      if (apiResults.error && apiResults.couchUser) {
        this.showPartialResult(apiResults);
      } else if (apiResults.error) {
        this.showUnreadableAccount(apiResults.error);
      } else {
        this.userDetails = apiResults;
        this.couchInfoText = JSON.stringify(apiResults.couchUser, null, 2);
        this.payProInfoText = JSON.stringify(apiResults.payProSubscriptionDetails, null, 2);
        this.updateCurrentStateSummary();
        if (apiResults.syncInfo) {
          if (isPayProSubscriptionSyncInfoIdentical(apiResults.syncInfo)) {
            this.syncedText = "Subscription data is up to date.";
          } else {
            this.syncedText = JSON.stringify(apiResults.syncInfo.differences, null, 2);
          }
        } else {
          this.syncedText = "No subscription data available.";
        }
      }
    } catch (error) {
      this.showUnreadableAccountFromError(error);
    } finally {
      this.loading = false;
      this.cdRef.markForCheck();
    }
    return null;
  }

  /**
   * Whatever we knew about this account a moment ago, we do not know it now:
   * drop it, and stop offering to act on it.
   *
   * The operator can read, but not act on, an account whose state we could not
   * establish: the current-state summary and every action button are cleared,
   * including those left over from an EARLIER successful load. That last part
   * is the whole point — this page re-reads the account after every support
   * action (`runSubscriptionActionAsync`), so when that re-read fails the
   * fields still hold the state from BEFORE the action. Leaving them would put
   * an authoritative-looking summary and a row of live buttons in front of an
   * operator, describing an account as it no longer is.
   *
   * @param whyNotShown what goes in the summary box in place of the state — a
   *                    plain sentence naming the reason. NOT left empty: the
   *                    same argument `updateCurrentStateSummary` makes for the
   *                    duplicate-account box applies here, an empty box reads
   *                    as "checked, nothing found".
   */
  private clearActionableState(whyNotShown: string) {
    this.userDetails = undefined;
    this.currentStateText = whyNotShown;
    this.canGrantFreeDays = false;
    this.canCancelSubscription = false;
    this.canResyncSubscription = false;
    this.canResumeSubscription = false;
    this.canSwitchPlan = false;
    this.canGrantLegacyPrice = false;
    // no user data means we could not ASK the matrix, which is not the same as
    // "every action is available" — leave the list empty rather than inventing
    // reasons for actions nobody evaluated.
    this.unavailableActions = [];
  }

  /**
   * Reading the account THREW. Decide the wording and what Sentry hears, then
   * say on the screen that the account could not be read.
   *
   * The shared policy lives here rather than in the `catch` for two reasons, and
   * the second one is the load-bearing one:
   *
   * - Reading this user calls a server endpoint that reads their `_users`
   *   record, so this path sees the #4283 "this server is behind that account's
   *   data" refusal — an expected, self-resolving condition that must say so
   *   rather than arrive as an anonymous failure with a Sentry report. That is
   *   what `describeErrorAndReportIfUnexpected` decides.
   * - `officer-screen-error-policy.test.ts` recognises a catch block as showing
   *   the operator a failure when it hands the caught error to a method on this
   *   page that calls the policy. With the display moved into
   *   `showUnreadableAccount`, a catch that only computed a string and passed it
   *   on matched none of that guard's patterns, so the guard stopped examining
   *   `loadAsync` altogether — green without having looked. Keeping the policy
   *   call in a helper the catch hands the raw error to is what keeps it visible.
   */
  private showUnreadableAccountFromError(error: unknown) {
    const reason = describeErrorAndReportIfUnexpected(
      error,
      this.auth.logging,
      untranslatedString(describeError(error)),
    );
    this.showUnreadableAccount(reason);
  }

  /**
   * The account could not be read at all — the call threw, or the server
   * answered with an error and no user record. Say so in every box that would
   * otherwise keep showing what the last successful read put there.
   *
   * @param reason the plain-language cause. A thrown read arrives here via
   *               `showUnreadableAccountFromError`, which runs the shared error
   *               policy first; the server's own error string arrives as it is.
   */
  private showUnreadableAccount(reason: string) {
    this.clearActionableState(`Not shown: this user could not be read (${reason}).`);

    this.couchInfoText = `Error: ${reason}`;
    this.payProInfoText = `Not read: this user could not be read (${reason}).`;
    this.syncedText = `Not compared: this user could not be read (${reason}).`;
    // say the check did not run rather than leaving the previous user's answer
    // — or an empty box, which would read as "checked, nothing found".
    this.relatedAccountsText = `Not checked: this user could not be read (${reason}).`;
    // an empty money card reads as "this customer was never charged", which is
    // the one wrong answer this card must never give.
    this.paymentHistoryText = `Could not load this user: ${reason}\nTreat the payment history as UNKNOWN.`;
  }

  // The server read the user but could not read PayPro. Show what it did
  // gather — the user record, the duplicate-account check and any Paddle
  // history — beside the error, instead of throwing it all away.
  //
  // Acting is still refused, for the reason `clearActionableState` explains.
  private showPartialResult(apiResults: AdminGetUserDetailsResult) {
    this.clearActionableState(`Not shown: PayPro could not be read (${apiResults.error}).`);

    this.couchInfoText = `Error: ${apiResults.error}\n\n${JSON.stringify(apiResults.couchUser, null, 2)}`;
    this.payProInfoText = `Could not read PayPro: ${apiResults.error}`;
    this.syncedText = `Not compared: PayPro could not be read (${apiResults.error}).`;
    this.updateRelatedAccountsText(apiResults);
    const unreadableText = unreadablePaymentHistoryText(apiResults);
    if (unreadableText !== undefined) {
      this.paymentHistoryText = unreadableText;
    } else {
      this.updatePaymentHistoryText(apiResults);
    }
  }

  async ionViewWillEnter() {
    this.userId = this.navParams.data.id;
  }

  async ionViewDidEnter() {
    void this.loadAsync();
  }

  // Admin action: re-read this customer's subscription FROM the payment provider
  // and write down whatever the provider says (#4248 Phase 3). Works for all four
  // providers now, so it must NOT assume a PayPro block exists — it used to read
  // `payProData.subscriptionId` unconditionally, which throws for a Paddle,
  // Apple or Google Play user. The backend derives the subscription id from the
  // user record itself, exactly like Grant Free Days and Cancel.
  async handleSyncSubscriptionButtonClick() {
    await this.runSubscriptionActionAsync<undefined>({
      // this only re-reads what the provider says, so it changes nothing on its
      // own and asks the operator nothing.
      askOperatorAsync: nothingToAskTheOperatorAsync,
      buildParams: () => ({
        action: 'Synchronize',
        userId: this.userId,
        sendCustomerNotification: false,
      }),
      failureTitle: 'Re-sync Failed',
      couldNotDo: 're-sync from the provider',
      successTitle: 'Re-sync Complete',
      // say what happened. "Nothing changed" and "we could not reach the
      // provider" look identical on a silent refresh, and confusing the two is
      // exactly how a stale account gets signed off as healthy.
      describeSuccess: result => {
        const sync = result as AdminChangeSubscriptionResult_Synchronize;
        const main = sync.updated
          ? 'The provider had newer information. The account has been updated to match.'
          : 'The provider agrees with what we already had. Nothing needed changing.';
        // a coach seat beside another licence subscription is re-read too (#5958)
        if (!sync.coachSeatReread) return main;
        return main + (sync.coachSeatReread.updated
          ? ' This included the coach\'s Paddle coaching subscription, which had newer information and was updated.'
          : ' This included the coach\'s Paddle coaching subscription, which already matched Paddle.');
      },
    });
  }

  // Admin action: prompt for a new email, then call the server to apply the change
  // (Firebase + CouchDB) and have PayPro send a portal login link to the OLD email.
  async handleChangeEmailButtonClick() {
    // ask for the new email
    let rawNewEmail = await this.alertCtrl.showAlertAsync<string | null>({
      title: untranslatedString('Change Email'),
      message: untranslatedString('Enter the new email address. A PayPro portal login link will be sent to the OLD email so the user can confirm/complete the change on the payment provider side.'),
      inputs: [
        { name: 'email', type: 'email', placeholder: untranslatedString('new@example.com'), value: '' }
      ],
      buttons: [
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: null },
        { text: untranslatedString('OK'), role: AlertButtonRole.Primary, result: data => (data['email'] as string) || null }
      ]
    });
    if (!rawNewEmail) {
      return;
    }
    // submit the already-normalized value so client display and server storage match
    let newEmail = normalizeEmail(rawNewEmail);
    // if the format is obviously wrong, bail (shared rule with server)
    if (!isAdminEmailShapeValid(newEmail)) {
      await this.alertCtrl.showAlertAsync<void>({
        title: untranslatedString('Invalid email'),
        message: untranslatedString('The email address must have a non-empty local and domain part around "@".'),
        buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary }]
      });
      return;
    }

    this.loading = true;
    this.cdRef.markForCheck();

    try {
      let apiParams: AdminChangeEmailParams = {
        userId: this.userId,
        newEmail,
      };
      let result = await this.cloudFunctions.adminChangeEmailAsync(apiParams);

      if (result.error) {
        await this.alertCtrl.showAlertAsync<void>({
          title: untranslatedString('Change Email Failed'),
          message: untranslatedString(result.error),
          buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary }]
        });
      } else {
        // if success, describe what happened with the PPG portal link
        let oldEmailDisplay = result.oldEmail || '(none)';
        let ppgMsg = !result.oldEmail
          ? `No previous email on file; PPG portal link was not sent.`
          : result.ppgPortalLinkSent
            ? `PPG portal login link sent to OLD email (${result.oldEmail}).`
            : `PPG portal link was NOT sent — please handle in PPG admin manually.`;
        await this.alertCtrl.showAlertAsync<void>({
          title: untranslatedString('Email Changed'),
          message: untranslatedString(`Email updated from ${oldEmailDisplay} to ${result.newEmail}.\n${ppgMsg}`),
          buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary }]
        });
      }

    } catch (error) {
      // adminChangeEmail can THROW, not only come back with `{ error }`: it reads
      // the target account's `_users` doc on the server, and since #4283 that read
      // REFUSES (HTTP 409) when the doc is newer than the live server understands.
      // without this catch the spinner cleared and the page did nothing at all, so
      // the admin could not tell "the email was changed" from "the call blew up" —
      // the same wrong-signoff risk the Re-sync handler above guards against.
      // shared policy (wording plus what reaches Sentry): see the helper.
      await this.showActionFailureAsync('Change Email Failed', 'change the email', error);
    }

    // The re-read is its own step, OUTSIDE the catch above: it reads the same
    // `_users` doc and can be refused on its own, and reporting that as
    // "Change Email Failed" would deny a change that already landed.
    try {
      await this.loadAsync();
    } catch (reloadError) {
      await this.showActionFailureAsync(
        'Could Not Refresh This Page',
        're-read this account',
        reloadError,
        'Reload the page to see its current state.',
      );
    } finally {
      this.loading = false;
      this.cdRef.markForCheck();
    }
  }

  // Build the formatted current-state summary and decide whether the Grant
  // Free Days button shows. The backend now handles every provider, so the
  // admin sees the provider, status, trial flag, and current next-charge /
  // expiry date BEFORE granting — read from the matching provider block.
  private updateCurrentStateSummary() {
    const user: CouchUserInfo | undefined = this.userDetails?.couchUser;
    if (!user) {
      // clear through the shared helper rather than by hand: it wipes EVERY
      // action flag, and a branch that cleared only some of them left the
      // other buttons on screen, still live, still decided from the account
      // read BEFORE this one (df-00f538def798).
      this.clearActionableState('No subscription data available.');
      // say the check did not run rather than leaving an empty box, which
      // would read as "checked, nothing found".
      this.relatedAccountsText = 'No user data, so the duplicate-account check did not run.';
      // unreachable on the current server contract (couchUser is set on every
      // non-error return), but set defensively: an empty money card is the one
      // wrong answer this card must never give.
      this.paymentHistoryText = 'No user data, so the payment history could not be read.';
      return;
    }

    // one shared builder feeds this page AND the firebase-cmd `user:sub`
    // command, so the two front-ends can never drift apart on what the state
    // actually is (#4248). It reads only non-secret fields.
    const summary = buildSubscriptionOpsSummary(user);

    // PayPro is the one case where the page knows better than the stored doc:
    // the authoritative next-charge date is the LIVE PayPro nextPayment, which
    // only this page fetches. Everywhere else the block's expiry is correct.
    const nextChargeOrExpiry = summary.provider === UserLicenseType.PayPro
      ? (this.userDetails?.payProSubscriptionDetails?.nextPayment ?? summary.expires_utc)
      : summary.expires_utc;

    const trialText = summary.inTrial === undefined ? 'n/a' : (summary.inTrial ? 'yes' : 'no');
    const lines = [
      `Provider: ${summary.provider ?? 'none'}`,
      `Status: ${summary.status ?? 'none'}`,
      `In trial: ${trialText}`,
      `Next charge / expiry: ${nextChargeOrExpiry ?? '(unknown)'}`,
      `Reconciled license expiry: ${summary.topLevelExpires_utc ?? '(unknown)'}`,
      // the shared builder decides when an absent id means "hidden credential"
      // rather than "no id", so both front-ends explain it the same way.
      `Provider subscription id: ${summary.providerSubscriptionId
        ?? (summary.providerSubscriptionIdHiddenReason
          ? `(${summary.providerSubscriptionIdHiddenReason})`
          : '(none)')}`,
    ];
    if (summary.scheduledChange) {
      lines.push(`Scheduled ${summary.scheduledChange.action} on: ${summary.scheduledChange.effectiveAt_utc}`);
    }
    // show every provider block, not just the active one, so a leftover block
    // from a provider the user migrated away from is visible.
    for (const block of summary.blocks) {
      const parts = [`status=${block.status ?? '?'}`];
      if (block.productId) {
        parts.push(`product=${block.productId}`);
      }
      if (block.expires_utc) {
        parts.push(`expires=${block.expires_utc}`);
      }
      // a pending Paddle change on THIS block (#4418) — a coach can hold a
      // personal block and a coaching seat, each with its own.
      if (block.scheduledChange) {
        parts.push(`pending=${block.scheduledChange.action}@${block.scheduledChange.effectiveAt_utc}`);
      }
      lines.push(`  ${block.isActiveProvider ? '*' : ' '} ${block.provider}: ${parts.join(' ')}`);
    }
    this.currentStateText = lines.join('\n');

    this.updateRelatedAccountsText(this.userDetails);
    this.updatePaymentHistoryText(this.userDetails);

    // Every action button is decided by the shared capability matrix, so the
    // button, the server guard and the CLI can never disagree. The capability
    // OBJECTS are kept, not just their booleans, because a refused action now
    // shows its reason where its button would have been (#4362).
    const capabilities = {
      // Grant Free Days used to carry a hand-written four-provider list here.
      // The matrix now mirrors what the server handler can actually serve
      // (#4362), so the list is gone and this reads the one source of truth.
      'Grant Free Days': resolveActionCapability('ChangeNextPaymentDate', user.licenseType, user),
      'Cancel Subscription': resolveActionCapability('CancelAtPeriodEnd', user.licenseType, user),
      'Resume Subscription': resolveActionCapability('Resume', user.licenseType, user),
      'Switch Plan': resolveActionCapability('SwitchPlan', user.licenseType, user),
      'Grant Old PayPro Price': resolveActionCapability('GrantLegacyPrice', user.licenseType, user),
      'Re-sync from Provider': resolveActionCapability('Synchronize', user.licenseType, user),
    };

    // The status condition below covers every provider, but the reason it
    // excludes Paused is Paddle-specific. Paused is only ever set from Paddle's
    // OWN side — Retain's cancellation-flow save offer, or a merchant action in
    // the Paddle dashboard; the app never pauses a subscription itself (#4189),
    // so do not go looking for an in-app pause flow. The Paddle grant path
    // PATCHes `next_billed_at`, which a paused subscription does not have, so
    // offering the button there would reliably fail. Support must have the
    // customer resume first, then grant.
    //
    // It stays HERE rather than moving into the matrix: the matrix is pure and
    // clock-free and deliberately does not encode a live-status rule.
    const grantableStatus = summary.status === SubscriptionStatus.Active || summary.status === SubscriptionStatus.Suspended;
    this.canGrantFreeDays = capabilities['Grant Free Days'].supported && grantableStatus;

    // The Cancel button shows only where we can actually cancel on the customer's
    // behalf — i.e. the web rails. We only offer it for a live (Active)
    // subscription: a Suspended sub is already on its way out, so cancelling
    // again is a no-op the operator does not need.
    this.canCancelSubscription = capabilities['Cancel Subscription'].supported
      && summary.status === SubscriptionStatus.Active;

    // Re-sync is offered whenever we hold something to ask the provider about —
    // no status condition. A subscription that looks Terminated to us is exactly
    // the case where the provider may disagree, so gating on "Active" would
    // withhold the button precisely when support needs it most.
    this.canResyncSubscription = capabilities['Re-sync from Provider'].supported;

    // Resume needs no status condition of its own — the matrix already refuses a
    // subscription that has nothing pending to undo.
    this.canResumeSubscription = capabilities['Resume Subscription'].supported;

    // Switch Plan likewise needs no status condition here — the matrix already
    // refuses every state where an immediate proration charge would be wrong.
    this.canSwitchPlan = capabilities['Switch Plan'].supported;

    // Grant the old PayPro price (#5084). No status condition here either — the
    // matrix refuses a non-Paddle customer, an ended subscription, and one that
    // already has a discount attached.
    this.canGrantLegacyPrice = capabilities['Grant Old PayPro Price'].supported;

    // Say WHY each withheld action is withheld. A refused capability always
    // carries a reason; the two status conditions above are ours rather than the
    // matrix's, so they get their own sentence instead of an empty line.
    this.unavailableActions = [];
    for (const [action, capability] of Object.entries(capabilities)) {
      if (capability.supported) {
        continue;
      }
      this.unavailableActions.push({ action, reason: capability.reason });
    }
    if (capabilities['Grant Free Days'].supported && !this.canGrantFreeDays) {
      this.unavailableActions.push({
        action: 'Grant Free Days',
        reason: `This subscription is '${summary.status ?? 'none'}'. Free days are granted by pushing out the next `
          + 'charge, which only works while the subscription is active or in a billing retry. '
          + 'A paused subscription has no next charge to move — have the customer resume it first.',
      });
    }
    if (capabilities['Cancel Subscription'].supported && !this.canCancelSubscription) {
      this.unavailableActions.push({
        action: 'Cancel Subscription',
        reason: `This subscription is '${summary.status ?? 'none'}', not active, so it is already on its way out `
          + 'and there is nothing left to cancel.',
      });
    }
  }

  /** Render the payments / refunds / chargebacks block (#4248 Phase 5).
   *
   *  Never renders a bare "no payments" when the lookup FAILED: "we could not
   *  read the history" and "this customer was never charged" are completely
   *  different answers to give someone asking where their money went. */
  private updatePaymentHistoryText(details: AdminGetUserDetailsResult | undefined) {
    if (details?.paymentHistoryError) {
      this.paymentHistoryText =
        `Could not load the payment history: ${details.paymentHistoryError}\n`
        + 'Treat this as UNKNOWN, not as "no payments".';
      return;
    }

    const history = details?.paymentHistory;
    if (history === undefined) {
      // No history was fetched. Say WHICH reason applies rather than assuming a
      // store purchase exists — a free user, one mid-provisioning, or a Paddle
      // user whose subscription id was never written back all land here too, and
      // sending an operator to App Store Connect for those wastes their time.
      const licenseType = details?.couchUser?.licenseType;
      const isStoreRail = licenseType === UserLicenseType.Apple || licenseType === UserLicenseType.GooglePlay;
      this.paymentHistoryText = isStoreRail
        ? 'Apple and Google Play purchases are recorded in the store, not in an API we can read '
          + '— check App Store Connect or the Play Console.'
        : 'No web subscription on file for this user, so there is no payment history to read.';
      return;
    }
    if (history.length === 0) {
      this.paymentHistoryText = 'The provider reports no payments for this subscription.';
      return;
    }

    // one shared renderer feeds this page AND the firebase-cmd history output,
    // so the two front-ends can never describe the same history differently.
    const lines = formatPaymentHistoryLines(history);
    if (details?.paymentHistoryTruncated) {
      // never let one page of rows read as the customer's whole history.
      lines.push('', 'WARNING: there is older history than this. The list above is not complete.');
    }
    if (details?.paymentHistoryPartialError) {
      // a different warning from truncation: a whole provider is missing.
      lines.push('', `WARNING: part of this customer's history could not be read (${details.paymentHistoryPartialError}).`,
        'Charges from one of their payment providers are MISSING from the list above.');
    }
    this.paymentHistoryText = lines.join('\n');
  }

  /** Render the "other accounts for this person" block. Never renders a bare
   *  "none found" when the lookup failed or was truncated — a duplicate that
   *  went unreported is exactly how a subscription ends up moved onto the
   *  wrong account. */
  private updateRelatedAccountsText(details: AdminGetUserDetailsResult | undefined) {
    if (details?.relatedAccountsError) {
      this.relatedAccountsText =
        `Could not check for other accounts: ${details.relatedAccountsError}\n`
        + 'Treat this as UNKNOWN, not as "no duplicates".';
      return;
    }

    const related = details?.relatedAccounts ?? [];
    const truncatedNote = details?.relatedAccountsTruncated
      ? '\nWARNING: the search hit its limit, so this list may be incomplete.'
      : '';
    // #4283: accounts whose _users doc was written by a newer server than the
    // one that ran the scan were skipped. Same warning as truncation, for the
    // same reason — those accounts were never examined, so the list below is
    // not the whole answer.
    const skippedTooNew = details?.relatedAccountsSkippedTooNew ?? 0;
    const skippedNote = skippedTooNew > 0
      ? `\nWARNING: ${skippedTooNew} account(s) could not be read (written by a newer server version), so this list may be incomplete.`
      : '';
    const incompleteNotes = `${truncatedNote}${skippedNote}`;

    if (related.length === 0) {
      this.relatedAccountsText = `No other accounts found for this person.${incompleteNotes}`;
      return;
    }

    // cap the rendered list — one person can legitimately own dozens of
    // accounts (a heavy "+tag" user), and an uncapped list is unreadable.
    const limited = limitRelatedAccountsForDisplay(related);
    const lines = limited.shown.map(account => {
      const reasons = account.matchedOn.map(reason => reason === 'email' ? 'same email' : 'same Paddle customer');
      return `${account.email ?? '(no email)'}  [${reasons.join(' + ')}]  uid=${account.uid}`;
    });
    if (limited.hiddenCount > 0) {
      lines.push(`… and ${limited.hiddenCount} more`);
    }
    this.relatedAccountsText = `${related.length} other account(s):\n${lines.join('\n')}${incompleteNotes}`;
  }

  // Admin action: undo a pending cancellation — the mirror image of the Cancel
  // button (#4248 Phase 4). Support cancels a subscription, the customer changes
  // their mind, and until now the only way back was to have them buy a new one.
  // Follows the same shape as every other action on this page.
  async handleResumeSubscriptionButtonClick() {
    await this.runSubscriptionActionAsync<true>({
      askOperatorAsync: () => {
        // a PAUSED coaching seat (#4418) gets its own cost line: un-pausing it
        // bills the coach a fresh period at once, and the coach-seat Cancel
        // refusal sends support here first.
        const coachResumeCost = this.userDetails?.couchUser
          ? describeCoachResumeCost(this.userDetails.couchUser)
          : undefined;
        return this.confirmActionAsync(
          'Resume Subscription',
          coachResumeCost
            ? `${coachResumeCost} Continue?`
            : 'This puts the subscription back to renewing normally: it undoes a pending '
              + 'cancellation, un-pauses a paused subscription, or renews a suspended one, '
              + 'depending on the state it is in. Note that renewing a suspended subscription '
              + 'may charge the customer straight away. Continue?',
          'Leave Cancelled',
          'Resume');
      },
      buildParams: () => ({
        action: 'Resume',
        userId: this.userId,
      }),
      failureTitle: 'Resume Subscription Failed',
      couldNotDo: 'resume the subscription',
      successTitle: 'Subscription Resumed',
      describeSuccess: () => 'The subscription will renew again as normal.',
    });
  }

  /**
   * Grant the old PayPro price (#5084).
   *
   * TWO round-trips on purpose. The first runs the server's dry run, which reads
   * the customer's live Paddle quote and works out the exact discount; the
   * confirmation then shows real figures, and only a second call applies them.
   * A single-step confirm would be asking an operator to approve an amount of
   * money nobody has seen yet — and unlike the plan switch beside it, here the
   * amount IS the decision.
   */
  async handleGrantLegacyPriceButtonClick() {
    await this.runSubscriptionActionAsync<AdminLegacyPriceGrantSummary>({
      // the opening step is the whole first round-trip: work the figures out,
      // then show them to the operator. What it returns is what the success
      // message below quotes back.
      askOperatorAsync: () => this.previewAndConfirmLegacyPriceAsync(),
      buildParams: () => ({
        action: 'GrantLegacyPrice',
        userId: this.userId,
      }),
      // the backend's refusal here includes the half-state notice, which
      // deliberately does NOT claim nothing happened.
      failureTitle: 'Grant Old Price Failed',
      couldNotDo: 'grant the old price',
      successTitle: 'Old Price Granted',
      describeSuccess: (_result, grant) => {
        const money = (minor: number) => formatMinorAmount(grant.currencyCode, minor);
        return `From their next billing period the customer pays ${money(grant.targetMinor)} instead of `
          + `${money(grant.quotedMinor)}. The monthly/yearly switch is now refused for them, because the `
          + 'grant only applies to the plan they are on — if they ask to switch, switch them and grant again.';
      },
    });
  }

  /**
   * The opening step of the legacy-price grant: run the server's dry run, then
   * ask the operator to approve the figures it came back with.
   *
   * Returns those figures to go ahead with, or `null` when nothing should be
   * applied — the operator declined, the server refused, the call threw, or the
   * dry run answered in a shape we do not understand.
   *
   * It keeps its own busy flag because it is a server call of its own, made
   * before the action proper starts. The confirmation is deliberately shown with
   * the spinner already cleared, exactly as it was.
   */
  private async previewAndConfirmLegacyPriceAsync(): Promise<AdminLegacyPriceGrantSummary | null> {
    this.loading = true;
    this.cdRef.markForCheck();

    let preview: AdminChangeSubscriptionResult;
    try {
      preview = await this.cloudFunctions.adminChangeSubscriptionAsync({
        action: 'GrantLegacyPrice',
        userId: this.userId,
        dryRun: true,
      });
    } catch (error) {
      await this.showActionFailureAsync('Grant Old Price Failed', 'work out the old price', error);
      return null;
    } finally {
      this.loading = false;
      this.cdRef.markForCheck();
    }

    if (preview.error) {
      await this.alertCtrl.showAlertAsync<void>({
        title: untranslatedString('Cannot Grant the Old Price'),
        message: untranslatedString(preview.error),
        buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary }],
      });
      return null;
    }
    const grant = (preview as AdminChangeSubscriptionResult_GrantLegacyPrice).grant;
    if (!grant) {
      // the dry run reported neither a refusal nor a plan — a shape we do not
      // understand. Never present that as a grant the operator can approve.
      await this.alertCtrl.showAlertAsync<void>({
        title: untranslatedString('Cannot Grant the Old Price'),
        message: untranslatedString(
          'The server reported no problem but did not say what the grant would be. '
          + 'Check the subscription in the Paddle dashboard before trying again.'),
        buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary }],
      });
      return null;
    }

    const money = (minor: number) => formatMinorAmount(grant.currencyCode, minor);
    const cycle = grant.months === 12 ? 'yearly' : 'monthly';
    const confirmed = await this.confirmActionAsync(
      'Grant the Old PayPro Price',
      `This customer is quoted ${money(grant.quotedMinor)} ${cycle}. `
      + `Granting takes ${money(grant.discountMinor)} off every future ${cycle} charge, `
      + `so they would pay ${money(grant.targetMinor)} instead. `
      + 'Nothing is charged or refunded now — it applies from their next billing period. '
      + 'Only grant this if the customer asked for it and was moved across from PayPro. Continue?',
      'Cancel',
      'Grant Old Price');
    return confirmed ? grant : null;
  }

  // Admin action: switch the customer between the monthly and yearly plan
  // (#4248 Phase 7) — the Paddle counterpart of the PayPro-only Change Product.
  //
  // The operator is deliberately not asked WHICH plan: the server derives the one
  // legitimate target from the configured monthly/yearly pair. So the worst a
  // mis-click can do is move the customer to the other standard plan, never onto
  // a coach-seat or legacy price. The confirmation says plainly that money moves
  // straight away, because it does — Paddle bills the prorated difference the
  // moment this runs.
  async handleSwitchPlanButtonClick() {
    await this.runSubscriptionActionAsync<true>({
      askOperatorAsync: () => this.confirmActionAsync(
        'Switch Plan',
        'This switches the customer between the monthly and yearly plan. '
        + 'Paddle settles the difference IMMEDIATELY: moving up charges the customer '
        + 'the prorated amount now, and moving down credits them. There is no preview '
        + 'of the exact amount. Make sure the customer has asked for this. Continue?',
        'Cancel',
        'Switch Plan'),
      buildParams: () => ({
        action: 'SwitchPlan',
        userId: this.userId,
      }),
      failureTitle: 'Switch Plan Failed',
      couldNotDo: 'switch the plan',
      successTitle: 'Plan Switched',
      describeSuccess: () => 'The customer is now on the other plan, and Paddle has settled the difference.',
    });
  }

  // Admin action: cancel the subscription at the END of the current billing
  // period, so the customer keeps access until then (#4248). Follows the Grant
  // Free Days pattern exactly: confirm → busy + markForCheck → callable → alert
  // carrying the backend's plain-language message → loadAsync refresh → finally
  // reset. The confirm prompt states the access-until-period-end promise so the
  // operator can see it being kept.
  async handleCancelSubscriptionButtonClick() {
    await this.runSubscriptionActionAsync<true>({
      askOperatorAsync: () => this.confirmCancelAsync(),
      buildParams: () => ({
        action: 'CancelAtPeriodEnd',
        userId: this.userId,
      }),
      failureTitle: 'Cancel Subscription Failed',
      couldNotDo: 'cancel the subscription',
      successTitle: 'Subscription Cancelled',
      describeSuccess: () =>
        'The subscription will not renew. The customer keeps access until the end of the current billing period.',
    });
  }

  /**
   * The Cancel confirmation. For an account holding a coaching seat (#4418) it
   * states the blast radius — how many clients lose Premium, and when — from the
   * server's one count, worded by the same shared helper `sub:cancel` uses. When
   * that count could not be read, the cancel is refused outright rather than
   * shown as "0 clients".
   */
  private async confirmCancelAsync(): Promise<true | null> {
    const user = this.userDetails?.couchUser;
    const coachImpact = user
      ? describeCoachCancelImpact(user, this.userDetails?.coachClientCounts, this.userDetails?.coachClientCountsError)
      : undefined;
    if (coachImpact?.refusal) {
      await this.alertCtrl.showAlertAsync<void>({
        title: untranslatedString('Cannot Cancel Yet'),
        message: untranslatedString(coachImpact.refusal),
        buttons: [{ text: untranslatedString('OK'), role: AlertButtonRole.Primary }],
      });
      return null;
    }
    const accessNote = 'The customer KEEPS access until then, and the subscription simply stops renewing.';
    return this.confirmActionAsync(
      'Cancel Subscription',
      'This cancels the subscription at the END of the current billing period. '
      + `${coachImpact ? coachImpact.description : accessNote} Continue?`,
      'Keep Subscription',
      'Cancel at Period End');
  }

  async handleGrantFreeDaysButtonClick() {
    await this.runSubscriptionActionAsync<number>({
      askOperatorAsync: () => this.askForFreeDayCountAsync(),
      // provider-agnostic: the backend reads the user's licenseType, picks the
      // right provider, and computes the target date itself.
      buildParams: daysToGrant => ({
        action: 'ChangeNextPaymentDate',
        userId: this.userId,
        daysToGrant,
      }),
      failureTitle: 'Grant Free Days Failed',
      couldNotDo: 'grant free days',
      successTitle: 'Free Days Granted',
      describeSuccess: (_result, daysToGrant) =>
        `Granted ${daysToGrant} free day(s). The next-charge / expiry date has been pushed out.`,
    });
  }

  /**
   * The opening step of Grant Free Days: ask how many days to grant.
   *
   * Returns the day count, or `null` when nothing should be granted — the
   * operator cancelled, or typed something that is not a number.
   */
  private async askForFreeDayCountAsync(): Promise<number | null> {
    const daysToGrant = await this.alertCtrl.showAlertAsync<number | null>({
      title: untranslatedString('Grant Free Days'), message: untranslatedString('How many days to grant?'),
      inputs: [
        { name: 'days', type: 'number', placeholder: untranslatedString('Days'), value: '14' }
      ],

      buttons: [
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: null },
        { text: untranslatedString('OK'), role: AlertButtonRole.Primary, result: data => parseInt(data['days']) }]
    });
    if (typeof daysToGrant !== 'number' || isNaN(daysToGrant)) {
      return null;
    }
    return daysToGrant;
  }
}
